/**
 * "Upload files" for the customer asset library (/content/assets): drag-and-drop or the file picker, several files at
 * once, per-file progress, cancel, retry, and type/size/quota errors before anything is sent. Behind the
 * assets.uploads flag: GET /v1/deliverables/assets/usage answers 404 feature_disabled while it is off, and then this
 * renders nothing. Built from @qm/ui components and brand tokens only (asset-upload.css), mobile first from 360 px.
 */
import { useEffect, useId, useRef, useState, useSyncExternalStore, type DragEvent } from 'react';
import { UPLOAD_ACCEPT, type UploadUsageView } from '@qm/contracts/asset-uploads';
import { Badge, Button, Heading, Panel, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';
import { bff, BffError, type FetchLike } from '../../../lib/bff';
import { sizeLabel } from './DriveImport';
import { Uploader, type FileLike, type ItemStatus, type UploadItem, type UploadTransport } from './uploader';
import './asset-upload.css';

const EMPTY: readonly UploadItem[] = [];
const TONE: Record<ItemStatus, 'neutral' | 'accent' | 'warning' | 'error' | 'success'> = {
  queued: 'neutral', uploading: 'accent', finalizing: 'accent', pending_scan: 'warning', ready: 'success', error: 'error', cancelled: 'neutral',
};

export function AssetUpload({ onUploaded }: { onUploaded: () => void }) {
  const usage = useResource<UploadUsageView>('/v1/deliverables/assets/usage');
  // Feature off (404) or not loaded yet: nothing, so the library works exactly as before.
  if (usage.status !== 'ready') return null;
  return <UploadPanel usage={usage.data} onUploaded={() => { usage.reload(); onUploaded(); }} />;
}

export function UploadPanel({ usage, onUploaded, transport, initiallyOpen = false }: { usage: UploadUsageView; onUploaded: () => void; transport?: UploadTransport; initiallyOpen?: boolean }) {
  const { t } = useApp();
  const f = useFormat();
  const [open, setOpen] = useState(initiallyOpen);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const settled = useRef(onUploaded);
  settled.current = onUploaded;
  // one queue for the panel's lifetime; fresh usage figures flow in through setUsage below
  const [uploader] = useState(() => new Uploader(usage, transport, { onSettled: (i) => { if (i.status !== 'error') settled.current(); } }));
  useEffect(() => { uploader.setUsage(usage); }, [uploader, usage]);
  const items = useSyncExternalStore(uploader.subscribe, uploader.snapshot, () => EMPTY);
  const panelId = useId();
  const hintId = useId();
  const size = (n: number) => sizeLabel(n, f.number);

  const addFiles = (list: FileList | null) => { if (list?.length) uploader.add(Array.from(list) as unknown as FileLike[]); };
  const onDrop = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); };
  const running = items.some((i) => i.status === 'uploading' || i.status === 'finalizing' || i.status === 'queued');

  return (
    <section className="qm-upload" aria-labelledby={`${panelId}-title`}>
      <div className="qm-upload__bar">
        <Heading level={2} id={`${panelId}-title`}>{t('deliverables.assets.upload.title')}</Heading>
        <Button variant="primary" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((o) => !o)}>
          {open ? t('deliverables.assets.upload.close') : t('deliverables.assets.upload.open')}
        </Button>
      </div>
      <Text tone="muted">
        {t('deliverables.assets.upload.usage', { used: size(usage.usedBytes + usage.reservedBytes), quota: size(usage.quotaBytes) })}
      </Text>
      <div id={panelId} hidden={!open}>
        <Panel className="qm-upload__panel">
          <div className="qm-upload__drop" data-dragging={dragging ? 'true' : 'false'}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}>
            <Text>{t('deliverables.assets.upload.drop')}</Text>
            <Button onClick={() => inputRef.current?.click()} aria-describedby={hintId}>{t('deliverables.assets.upload.choose')}</Button>
            <input ref={inputRef} className="qm-sr-only" type="file" multiple accept={UPLOAD_ACCEPT} tabIndex={-1} aria-hidden="true"
              onChange={(e) => { addFiles(e.currentTarget.files); e.currentTarget.value = ''; }} />
            <Text tone="muted" id={hintId}>{t('deliverables.assets.upload.hint', { max: size(usage.maxFileBytes) })}</Text>
          </div>
          {items.length ? (
            <ul className="qm-upload__list" aria-live="polite" aria-busy={running}>
              {items.map((i) => (
                <li key={i.key} className="qm-upload__item">
                  <div className="qm-upload__row">
                    <bdi className="qm-upload__name">{f.text(i.name)}</bdi>
                    <Badge tone={TONE[i.status]}>{t(`deliverables.assets.upload.status.${i.status}`)}</Badge>
                  </div>
                  {i.status === 'uploading' || i.status === 'finalizing' ? (
                    <progress className="qm-upload__progress" max={i.size || 1} value={i.loaded}
                      aria-label={t('deliverables.assets.upload.progress', { name: f.text(i.name) })} />
                  ) : null}
                  <Text tone="muted" className="qm-upload__meta">
                    {i.status === 'uploading' ? t('deliverables.assets.upload.sent', { sent: size(i.loaded), total: size(i.size) }) : size(i.size)}
                  </Text>
                  {i.errorKey ? <Text role="alert" className="qm-upload__error">{t(i.errorKey)}</Text> : null}
                  <div className="qm-upload__actions">
                    {i.status === 'queued' || i.status === 'uploading' ? (
                      <Button onClick={() => uploader.cancel(i.key)}>{t('deliverables.assets.upload.cancel')}<span className="qm-sr-only"> {f.text(i.name)}</span></Button>
                    ) : null}
                    {i.status === 'error' && i.retryable ? <Button onClick={() => uploader.retry(i.key)}>{t('deliverables.assets.upload.retry')}<span className="qm-sr-only"> {f.text(i.name)}</span></Button> : null}
                    {i.status === 'error' || i.status === 'cancelled' || i.status === 'ready' || i.status === 'pending_scan' ? (
                      <Button onClick={() => uploader.remove(i.key)}>{t('deliverables.assets.upload.remove')}<span className="qm-sr-only"> {f.text(i.name)}</span></Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </Panel>
      </div>
    </section>
  );
}

/** DELETE /v1/deliverables/assets/:id (soft delete; the server purges the bytes). Returns an i18n error key or null. */
export async function deleteAsset(id: string, fetchFn?: FetchLike): Promise<string | null> {
  try {
    await bff(`/v1/deliverables/assets/${encodeURIComponent(id)}`, { method: 'DELETE' }, fetchFn);
    return null;
  } catch (e) {
    if (e instanceof BffError && e.status === 404) return 'deliverables.assets.delete.error.gone';
    if (e instanceof BffError && (e.status === 401 || e.status === 403)) return 'deliverables.assets.upload.error.forbidden';
    return 'deliverables.assets.delete.error.generic';
  }
}

/**
 * "Delete" for one asset row, with a confirm dialog (native <dialog>: modal, focus kept inside, Esc cancels). Shown
 * only while uploads are enabled (the list passes it only when GET /usage answered).
 */
export function DeleteAssetButton({ id, title, onDeleted, fetchFn }: { id: string; title: string; onDeleted: () => void; fetchFn?: FetchLike }) {
  const { t } = useApp();
  const f = useFormat();
  const ref = useRef<HTMLDialogElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const name = f.text(title);
  const close = () => { setError(null); ref.current?.close(); };
  const confirm = async () => {
    setBusy(true); setError(null);
    const err = await deleteAsset(id, fetchFn);
    setBusy(false);
    if (err && err !== 'deliverables.assets.delete.error.gone') { setError(err); return; }
    ref.current?.close();
    onDeleted();
  };
  return (
    <>
      <Button onClick={() => ref.current?.showModal()}>{t('deliverables.assets.delete.button')}<span className="qm-sr-only"> <bdi>{name}</bdi></span></Button>
      <dialog ref={ref} className="qm-upload__dialog" role="alertdialog" aria-labelledby={titleId} onCancel={() => setError(null)}>
        <Heading level={2} id={titleId}>{t('deliverables.assets.delete.title')}</Heading>
        <Text><bdi>{name}</bdi></Text>
        <Text tone="muted">{t('deliverables.assets.delete.body')}</Text>
        {error ? <Text role="alert" className="qm-upload__error">{t(error)}</Text> : null}
        <div className="qm-upload__actions">
          <Button onClick={close} autoFocus>{t('deliverables.assets.delete.cancel')}</Button>
          <Button variant="primary" disabled={busy} onClick={() => void confirm()}>{t('deliverables.assets.delete.confirm')}</Button>
        </div>
      </dialog>
    </>
  );
}
